import { useState, useEffect, useCallback } from 'react'
import type { Category } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { budgetsApi, categoriesApi } from '../lib/api'
import { CategoryCombobox } from '../components/CategoryCombobox'
import { btnIconAccent, btnIconDanger, btnLink, btnLinkMuted, btnNav, btnPrimary, btnSecondary, modalTitle, pageSubtitle, pageTitle } from '../lib/ui'

interface BudgetItem {
  id: string
  categoryId: string
  categoryName: string
  amount: number
  actual: number
  remaining: number
  ratio: number
}

interface BudgetData {
  year: number
  month: number
  totalBudget: number
  totalExpense: number
  budgets: BudgetItem[]
}

export default function Budget() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const [budgetData, setBudgetData] = useState<BudgetData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [categories, setCategories] = useState<Category[]>([])

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingAmount, setEditingAmount] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [newCategoryId, setNewCategoryId] = useState('')
  const [newAmount, setNewAmount] = useState('')
  const [modalError, setModalError] = useState<string | null>(null)
  const [modalSaving, setModalSaving] = useState(false)

  const [copyMsg, setCopyMsg] = useState<string | null>(null)

  const token = accessToken ?? ''

  const fetchData = useCallback(() => {
    if (!token) return
    setLoading(true)
    setError(null)
    budgetsApi.list(token, year, month)
      .then((res) => setBudgetData(res.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setLoading(false))
  }, [token, year, month, t])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (!token) return
    categoriesApi.list(token)
      .then((res) => setCategories(res.data))
      .catch(() => undefined)
  }, [token])

  const prevMonth = () => {
    if (month === 1) { setYear((y) => y - 1); setMonth(12) }
    else setMonth((m) => m - 1)
  }
  const nextMonth = () => {
    if (month === 12) { setYear((y) => y + 1); setMonth(1) }
    else setMonth((m) => m + 1)
  }

  const handleEdit = (item: BudgetItem) => {
    setEditingId(item.id)
    setEditingAmount(String(item.amount))
  }

  const handleEditSave = async (id: string) => {
    const amount = parseInt(editingAmount, 10)
    if (isNaN(amount) || amount < 0) return
    try {
      await budgetsApi.update(token, id, amount)
      setEditingId(null)
      fetchData()
    } catch {
      // ignore
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm(t('budget_delete_confirm'))) return
    try {
      await budgetsApi.delete(token, id)
      fetchData()
    } catch {
      // ignore
    }
  }

  const handleCopyToNext = async () => {
    try {
      const res = await budgetsApi.copy(token, year, month)
      setCopyMsg(
        t('budget_copy_done')
          .replace('{copied}', String(res.data.copied))
          .replace('{skipped}', String(res.data.skipped)),
      )
      setTimeout(() => setCopyMsg(null), 3000)
    } catch (err: unknown) {
      setCopyMsg(err instanceof Error ? err.message : t('budget_copy_error'))
      setTimeout(() => setCopyMsg(null), 3000)
    }
  }

  const usedCategoryIds = new Set(budgetData?.budgets.map((b) => b.categoryId) ?? [])
  const availableCategories = categories.filter((c) => !usedCategoryIds.has(c.id))

  const handleModalOpen = () => {
    setNewCategoryId(availableCategories[0]?.id ?? '')
    setNewAmount('')
    setModalError(null)
    setModalOpen(true)
  }

  const handleModalSave = async () => {
    const amount = parseInt(newAmount, 10)
    if (!newCategoryId || isNaN(amount) || amount <= 0) {
      setModalError(t('budget_modal_invalid'))
      return
    }
    setModalSaving(true)
    setModalError(null)
    try {
      await budgetsApi.create(token, { categoryId: newCategoryId, year, month, amount })
      setModalOpen(false)
      fetchData()
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setModalSaving(false)
    }
  }

  const totalRatio = budgetData && budgetData.totalBudget > 0
    ? budgetData.totalExpense / budgetData.totalBudget
    : 0

  return (
    <div>
      <h1 className={pageTitle}>{t('budget_title')}</h1>
      <p className={pageSubtitle}>{t('budget_subtitle')}</p>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          onClick={prevMonth}
          className={btnNav}
        >
          ◀
        </button>
        <span className="text-base font-semibold text-gray-900 min-w-[7rem] text-center dark:text-gray-100">
          {t('common_year_month').replace('{year}', String(year)).replace('{month}', String(month))}
        </span>
        <button
          onClick={nextMonth}
          className={btnNav}
        >
          ▶
        </button>
        <button
          onClick={handleModalOpen}
          className={`${btnPrimary} ml-2`}
        >
          {t('budget_add')}
        </button>
        <button
          onClick={() => { void handleCopyToNext() }}
          className={btnSecondary}
        >
          {t('budget_copy_next')}
        </button>
        {copyMsg && (
          <span className="text-sm text-indigo-600 dark:text-indigo-400">{copyMsg}</span>
        )}
      </div>

      {budgetData && budgetData.totalBudget > 0 && (
        <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
          <div className="flex justify-between text-sm text-gray-600 mb-2 dark:text-gray-300">
            <span>{t('budget_total_label')}</span>
            <span>
              {formatJPY(budgetData.totalExpense)} / {formatJPY(budgetData.totalBudget)}
              <span className="ml-2 text-gray-400 dark:text-gray-500">({Math.round(totalRatio * 100)}%)</span>
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 dark:bg-gray-700">
            <div
              className={`h-3 rounded-full ${totalRatio >= 1 ? 'bg-red-500' : totalRatio >= 0.8 ? 'bg-yellow-400' : 'bg-green-500'}`}
              style={{ width: `${Math.min(totalRatio * 100, 100)}%` }}
            />
          </div>
        </div>
      )}

      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto dark:bg-gray-900 dark:border-gray-800">
        {loading ? (
          <div className="p-4 text-center text-gray-400 text-sm dark:text-gray-500">{t('common_loading')}</div>
        ) : error ? (
          <div className="p-4 text-center text-red-600 text-sm dark:text-red-400">{error}</div>
        ) : !budgetData || budgetData.budgets.length === 0 ? (
          <div className="p-4 text-center text-gray-400 text-sm dark:text-gray-500">{t('budget_empty')}</div>
        ) : (
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('budget_col_category')}</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('budget_col_amount')}</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('budget_col_actual')}</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('budget_col_remaining')}</th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36 dark:text-gray-400">{t('budget_col_ratio')}</th>
                <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('budget_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {budgetData.budgets.map((item) => (
                <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800">
                  <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100">{item.categoryName}</td>
                  <td className="px-3 py-1.5 text-sm text-gray-900 text-right dark:text-gray-100">
                    {editingId === item.id ? (
                      <input
                        type="number"
                        value={editingAmount}
                        onChange={(e) => setEditingAmount(e.target.value)}
                        className="w-28 text-right border border-indigo-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-indigo-600 dark:bg-gray-800 dark:text-gray-100"
                        autoFocus
                      />
                    ) : (
                      formatJPY(item.amount)
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-sm text-gray-700 text-right dark:text-gray-300">{formatJPY(item.actual)}</td>
                  <td className={`px-3 py-1.5 text-sm font-medium text-right ${item.remaining < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatJPY(item.remaining)}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                        <div
                          className={`h-2 rounded-full ${item.ratio >= 1 ? 'bg-red-500' : item.ratio >= 0.8 ? 'bg-yellow-400' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(item.ratio * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-10 text-right dark:text-gray-400">{Math.round(item.ratio * 100)}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {editingId === item.id ? (
                        <>
                          <button
                            onClick={() => { void handleEditSave(item.id) }}
                            className={btnLink}
                          >
                            {t('common_save')}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className={btnLinkMuted}
                          >
                            {t('common_cancel')}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(item)}
                            className={btnIconAccent}
                            title={t('common_edit')}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => { void handleDelete(item.id) }}
                            className={btnIconDanger}
                            title={t('common_delete')}
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 dark:bg-gray-900">
            <h2 className={`${modalTitle} mb-4`}>{t('budget_add')}</h2>
            {availableCategories.length === 0 ? (
              <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{t('budget_modal_no_categories')}</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('budget_col_category')}</label>
                  <CategoryCombobox
                    categories={availableCategories}
                    value={newCategoryId}
                    onChange={setNewCategoryId}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('budget_modal_amount_label')}</label>
                  <input
                    type="number"
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                    placeholder={t('budget_modal_amount_placeholder')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  />
                </div>
              </div>
            )}
            {modalError && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{modalError}</p>}
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className={btnSecondary}
              >
                {t('common_cancel')}
              </button>
              {availableCategories.length > 0 && (
                <button
                  onClick={() => { void handleModalSave() }}
                  disabled={modalSaving}
                  className={btnPrimary}
                >
                  {modalSaving ? t('common_saving') : t('common_save')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
